// ─────────────────────────────────────────────────────────────────────────
// emptyText — what the detail view shows where there is no value.
//
// THE PROBLEM
// Everything empty rendered as one global "-" (detailViewDefaults.
// emptyPlaceholder). A dash tells the reader nothing: it cannot distinguish
// "this candidate has no work experience" from "this field was never part of
// the form", and it reads as a rendering fault rather than as information.
// Worse, an addRow group with zero rows rendered as an EMPTY CARD — no text at
// all.
//
// THE RESOLUTION ORDER (most specific wins)
//   1. field.emptyText          — Detail Groups, per field
//   2. group.emptyText          — Detail Groups, per group (used when a
//                                 repeatable group has NO rows)
//   3. defaults.emptyTexts[<render type>] — tenant-wide, per kind of value
//   4. defaults.emptyTexts.default
//   5. defaults.emptyPlaceholder — the legacy single value, kept as the final
//      fallback so nothing regresses before any of the above is configured
//
// Nothing here knows a module or a field name; (1) and (2) are config and (3)
// keys off the render type the field already declares.
// ─────────────────────────────────────────────────────────────────────────
import { getDefaults } from '../../services/detailDefaults';

// Sensible built-ins, so the improvement is visible with ZERO configuration and
// an admin only overrides what they disagree with. Keyed by render type.
export const BUILT_IN_EMPTY_TEXTS = Object.freeze({
  document: 'No document uploaded',
  documents: 'No documents uploaded',
  date: 'Not provided',
  phone: 'No contact number added',
  email: 'No email address added',
  link: 'No link added',
  url: 'No link added',
  tags: 'None added',
  multitag: 'None added',
  skills: 'No skills added',
  currency: 'Not provided',
  money: 'Not provided',
  number: 'Not provided',
  boolean: 'Not specified',
  default: 'Not provided',
});

const text = (value) => {
  const s = value === null || value === undefined ? '' : String(value).trim();
  return s === '' ? null : s;
};

/**
 * resolveEmptyText — the text for a single empty FIELD.
 *
 * @param {object} field         the detail field config (carries emptyText, type/renderType)
 * @param {string} [renderType]  the resolved render type, when the caller already computed it
 * @param {object} [defaults]    injectable for tests
 */
export function resolveEmptyText(field, renderType, defaults = getDefaults()) {
  const own = text(field?.emptyText ?? field?.renderOptions?.emptyText);
  if (own) return own;

  const key = String(renderType ?? field?.renderType ?? field?.type ?? 'default').toLowerCase();
  const configured = defaults?.emptyTexts ?? {};

  return text(configured[key])
    ?? text(BUILT_IN_EMPTY_TEXTS[key])
    ?? text(configured.default)
    ?? text(BUILT_IN_EMPTY_TEXTS.default)
    // The legacy single placeholder is LAST, not first: a tenant that never
    // touched it still has "-" saved, and honouring that ahead of the built-ins
    // would mean this whole feature silently did nothing for existing tenants.
    ?? text(defaults?.emptyPlaceholder)
    ?? '—';
}

/**
 * resolveGroupEmptyText — the text for a repeatable group that has NO rows.
 * This is the "No experience added" case from the requirement: previously an
 * empty card with nothing in it.
 */
export function resolveGroupEmptyText(group, defaults = getDefaults()) {
  const own = text(group?.emptyText);
  if (own) return own;

  const configured = defaults?.emptyTexts ?? {};
  if (text(configured.group)) return text(configured.group);

  // Derived from the group's own label, so a NEW repeatable group gets a
  // sentence that reads correctly with no configuration at all:
  // "Work Experience" → "No work experience added".
  const label = text(group?.label ?? group?.name);
  if (label) return `No ${label.toLowerCase()} added`;
  return text(BUILT_IN_EMPTY_TEXTS.default) ?? '—';
}
